import { Injectable, Injector, effect, inject } from '@angular/core';

import { MenuModel } from '@models/menu';
import { FormParams, ImportSOVParams, TransitionFormParams } from '@models/module/entityInstance';
import { AttachmentModel } from '@models/module/fields/main';
import { DmTransitionParams } from '@module-instance/dynamic-module/common';
import { PdfMarkupModel } from '@module-instance/pdf-markups/common';
import { PPFormParams } from '@module-instance/progress-payment/common';
import { SovItemFormParams } from '@module-instance/sov/common';
import { DocumentDialogData, PdfHandlerProps, PdfMarkupDialogProps } from '@ng2/services/angular-js.model';
import { TenantStore } from '@signal-store/index';
import { UserStore } from '@signal-store/user.store';
import { ContextInfo, ContextTree } from '@system/context';

type LegacyModuleDefinitionRow = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class NgToJsService {
  private userStore = inject(UserStore);
  private tenantStore = inject(TenantStore);
  private angularJsServiceInstance: any;
  private injector = inject(Injector);

  constructor() {
    effect(() => {
      const userData = this.userStore.userData();
      if (userData) {
        if (userData.isLoading) {
          this.angularJsService.resetLoadingMenuFlags();
        }
        this.angularJsService.setUser(userData);
      }
    });
    effect(() => {
      const tenants = this.tenantStore.availableTenants();
      if (tenants) {
        this.angularJsService.setTenants(tenants);
      }
    });
    effect(() => {
      const modules = this.userStore.menu.items();
      if (modules) {
        this.angularJsService.setModules(modules);
      }
    });
    effect(() => {
      const selectedTenant = this.tenantStore.selectedTenant();
      if (selectedTenant) {
        this.angularJsService.setActiveTenant(selectedTenant?.id);
      }
    });
  }

  private get angularJsService() {
    if (!this.angularJsServiceInstance) {
      this.angularJsServiceInstance = this.injector.get('AngularJsUserService', null);
      if (!this.angularJsServiceInstance) {
        throw new Error('AngularJs service is not available.');
      }
    }
    return this.angularJsServiceInstance;
  }

  changeContext(context: ContextInfo, addLastUsedContract: boolean = true) {
    this.angularJsService.changeContext(context, addLastUsedContract);
  }

  contextDataLoaded(contextTree: ContextTree) {
    this.angularJsService.setContextTree(contextTree);
  }

  goToVoid() {
    this.angularJsService.goToVoid();
  }

  /* 
  The following methods are used to open a dialog from angularJs.
  To add a new method, you need to add a new method in the angular-js.service.js, and call it from here.
  Also, there is possibility to listen for the dialog close event (even send dialogData) with an effect on userStore.
  To achieve this, you need to call:
        dialog.show(...).then(() => jsToNg.dialogClosed(_YOUR_EXPECTED_DATA_))

  example of effect:
        dialogEffect = effect(() => {
            const dialogData = this.userService().dialogData();
            if (dialogData == _YOUR_EXPECTED_DATA_) {
                ...do something,
            }
        });
   */
  editContract(item?: any) {
    this.angularJsService.editContract(item);
  }

  openModuleSettings(module: MenuModel) {
    this.angularJsService.openModuleSettings(module);
  }

  openModuleDefinitionEditor(module: LegacyModuleDefinitionRow | null, typeId?: number) {
    this.angularJsService.openModuleDefinitionEditor(module, typeId);
  }

  openDuplicateModule(module: LegacyModuleDefinitionRow | null, exportList?: LegacyModuleDefinitionRow[]) {
    this.angularJsService.openDuplicateModule(module, exportList);
  }

  openImportModuleDefinition() {
    this.angularJsService.openImportModuleDefinition();
  }

  downloadDocuments(documents: DocumentDialogData[]) {
    this.angularJsService.downloadRows(documents);
  }

  initPdfHandler(props: PdfHandlerProps) {
    this.angularJsService.initPdfHandler(props);
  }

  openAttachment(attachments: AttachmentModel[], id: number) {
    this.angularJsService.openAttachment(attachments, id);
  }

  executeTransition(params: DmTransitionParams) {
    this.angularJsService.executeTransition(params);
  }

  emailDocuments(docs: DocumentDialogData[]) {
    this.angularJsService.emailDocuments(docs);
  }

  printDocuments(docs: DocumentDialogData[]) {
    this.angularJsService.printDocuments(docs);
  }

  importDocuments(moduleId: number, moduleCode: string) {
    this.angularJsService.openImportEntitiesDialog({ moduleId, moduleCode });
  }

  openReportsDialog(moduleCode: string) {
    this.angularJsService.openReportsDialog({ moduleCode });
  }

  openTransitionForm(params: TransitionFormParams) {
    this.angularJsService.openTransitionForm(params);
  }

  openSOVI(params: SovItemFormParams, editable: boolean) {
    this.angularJsService.openSOVI(params, editable);
  }

  importSOVWithCategories(params: ImportSOVParams) {
    this.angularJsService.importSOVWithCategories(params);
  }

  openSOVForm(params: FormParams) {
    this.angularJsService.openSOVForm(params);
  }

  openPPForm(params: PPFormParams) {
    this.angularJsService.openPPForm(params);
  }

  closeDialog() {
    this.angularJsService.closeDialog();
  }

  downloadLogs() {}

  printLogs() {}

  openModuleReports() {}

  openPdfMarkup({ documentId, fileId, name }: PdfMarkupModel) {
    const markupProps: PdfMarkupDialogProps = {
      documentId,
      fileId,
      title: name,
      entityInstanceId: this.userStore.contract().entityInstanceId,
    };
    this.angularJsService.openPdfMarkup(markupProps);
  }

  openAddPdfMarkup() {
    this.angularJsService.addNewPdfMarkup();
  }

  showDialogToSaveAttachments(isProjectFile: boolean, attachmentIds: number[], attachments: AttachmentModel[]) {
    this.angularJsService.showDialogToSaveAttachments(isProjectFile, attachmentIds, attachments);
  }

  closeAllDialogs() {
    this.angularJsService.closeAllDialogs();
  }
}
